import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "./api";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Folder,
  Wrench,
  Building2,
} from "lucide-react";

function AllAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAssets, setOpenAssets] = useState(() => new Set());
  const [openNodes, setOpenNodes] = useState(() => new Set());

  // ✅ NODE EDIT
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [nodeDraft, setNodeDraft] = useState({}); // { [nodeId]: "draft text" }

  // ✅ NODE ADD
  const [adding, setAdding] = useState(null); // { assetId, parentId, nodeType, name }

  // ✅ ASSET EDIT
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [assetDraft, setAssetDraft] = useState({
    asset_name: "",
    location: "",
    machine_type: "",
    line: "",
    description: "",
  });

  const fetchTree = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/assets/tree");
      const list = Array.isArray(res.data) ? res.data : [];
      setAssets(list);
    } catch (err) {
      console.error("Error fetching /api/assets/tree", err);
      alert(
        "❌ Failed to load Assets Tree. Make sure backend has GET /api/assets/tree"
      );
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  // build children maps per asset
  const childrenMapByAsset = useMemo(() => {
    const byAsset = new Map();

    for (const a of assets) {
      const nodes = Array.isArray(a.nodes) ? a.nodes : [];
      const map = new Map();

      for (const n of nodes) {
        const key = n.parent_id ?? null;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(n);
      }

      for (const [k, arr] of map.entries()) {
        arr.sort(
          (x, y) =>
            (x.sort_order ?? 0) - (y.sort_order ?? 0) ||
            (x.id ?? 0) - (y.id ?? 0)
        );
        map.set(k, arr);
      }

      byAsset.set(a.id, map);
    }

    return byAsset;
  }, [assets]);

  const toggleAsset = (id) => {
    setOpenAssets((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleNode = (id) => {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ===========================
  // ✅ ASSET EDIT / DELETE
  // ===========================
  const startEditAsset = (asset) => {
    setEditingAssetId(asset.id);
    setAssetDraft({
      asset_name: asset.asset_name || "",
      location: asset.location || "",
      machine_type: asset.machine_type || "",
      line: asset.line || "",
      description: asset.description || "",
    });

    // close other modes
    setEditingNodeId(null);
    setAdding(null);
  };

  const cancelEditAsset = () => {
    setEditingAssetId(null);
    setAssetDraft({
      asset_name: "",
      location: "",
      machine_type: "",
      line: "",
      description: "",
    });
  };

  const saveEditAsset = async (assetId) => {
    const payload = {
      asset_name: (assetDraft.asset_name || "").trim(),
      location: (assetDraft.location || "").trim(),
      machine_type: (assetDraft.machine_type || "").trim(),
      line: (assetDraft.line || "").trim(),
      description: assetDraft.description || "",
    };

    if (!payload.asset_name || !payload.line || !payload.machine_type) {
      return alert("Asset Name, Line, and Machine Type are required");
    }

    try {
      await api.put(`/api/assets/${assetId}`, payload);

      // fast local update
      setAssets((prev) =>
        prev.map((a) =>
          a.id === assetId ? { ...a, ...payload } : a
        )
      );

      cancelEditAsset();
      fetchTree();
    } catch (err) {
      console.error("Error updating asset", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to update asset: ${msg}`);
    }
  };

  const deleteAsset = async (assetId) => {
    const ok = window.confirm(
      "Delete this asset?\n\n⚠️ This will also delete its tree nodes (sections/parts) under it."
    );
    if (!ok) return;

    try {
      await api.delete(`/api/assets/${assetId}`);

      // update UI
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      setOpenAssets((prev) => {
        const next = new Set(prev);
        next.delete(assetId);
        return next;
      });

      if (editingAssetId === assetId) cancelEditAsset();
    } catch (err) {
      console.error("Error deleting asset", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to delete asset: ${msg}`);
    }
  };

  // ===========================
  // ✅ NODE EDIT (no 1-char bug)
  // ===========================
  const startEditNode = (node) => {
    setEditingNodeId(node.id);
    setNodeDraft((prev) => ({ ...prev, [node.id]: node.name || "" }));
    setAdding(null);
    setEditingAssetId(null);
  };

  const cancelEditNode = () => {
    setEditingNodeId(null);
  };

  const saveEditNode = async (nodeId) => {
    const name = (nodeDraft[nodeId] ?? "").trim();
    if (!name) return alert("Name is required");

    try {
      await api.put(`/api/asset-nodes/${nodeId}`, { name });

      // update UI locally + refresh
      setAssets((prev) =>
        prev.map((a) => ({
          ...a,
          nodes: (a.nodes || []).map((n) =>
            n.id === nodeId ? { ...n, name } : n
          ),
        }))
      );

      setEditingNodeId(null);
      setNodeDraft((prev) => {
        const copy = { ...prev };
        delete copy[nodeId];
        return copy;
      });

      fetchTree();
    } catch (err) {
      console.error("Error updating node", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to update node: ${msg}`);
    }
  };

  const deleteNode = async (nodeId) => {
    if (!window.confirm("Delete this item (and all children under it)?")) return;
    try {
      await api.delete(`/api/asset-nodes/${nodeId}`);
      fetchTree();
    } catch (err) {
      console.error("Error deleting node", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to delete node: ${msg}`);
    }
  };

  // ===========================
  // ✅ NODE ADD
  // ===========================
  const openAdd = ({ assetId, parentId, nodeType }) => {
    setAdding({ assetId, parentId: parentId ?? null, nodeType, name: "" });
    setEditingNodeId(null);
    setEditingAssetId(null);
  };

  const cancelAdd = () => setAdding(null);

  const createNode = async () => {
    if (!adding?.assetId || !adding?.nodeType) return;

    const name = (adding.name || "").trim();
    if (!name) return alert("Name is required");

    try {
      await api.post(`/api/assets/${adding.assetId}/nodes`, {
        node_type: adding.nodeType,
        name,
        parent_id: adding.parentId,
        sort_order: 0,
      });

      setAdding(null);
      fetchTree();
    } catch (err) {
      console.error("Error creating node", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to create ${adding.nodeType}: ${msg}`);
    }
  };

  // ===========================
  // NODE ROW UI
  // ===========================
  const NodeRow = ({ assetId, node, level }) => {
    const map = childrenMapByAsset.get(assetId);
    const kids = map?.get(node.id) || [];
    const hasKids = kids.length > 0;

    const isOpen = openNodes.has(node.id);
    const isEditing = editingNodeId === node.id;

    const typeConfig = {
      section: { label: "Section", icon: <Folder size={16} />, color: "#3b82f6" },
      part: { label: "Part", icon: <Wrench size={16} />, color: "#8b5cf6" },
    };

    const config =
      typeConfig[node.node_type] || {
        label: "Node",
        icon: <Wrench size={16} />,
        color: "#64748b",
      };

    const canToggle = hasKids;

    return (
      <div style={{ marginLeft: level > 0 ? 24 : 0, position: "relative" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 6,
            borderRadius: 10,
            border: "1px solid rgba(148,163,184,0.2)",
            background: isEditing
              ? "rgba(59,130,246,0.08)"
              : "rgba(255,255,255,0.04)",
            transition: "all 0.2s ease",
          }}
        >
          <button
            type="button"
            className="icon-btn ghost"
            onClick={() => canToggle && toggleNode(node.id)}
            title={canToggle ? (isOpen ? "Collapse" : "Expand") : "No children"}
            style={{
              visibility: canToggle ? "visible" : "hidden",
              width: 24,
              height: 24,
              padding: 4,
            }}
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 6,
              background: `${config.color}15`,
              color: config.color,
              border: `1px solid ${config.color}30`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            {config.icon}
            {config.label}
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 200,
              cursor: canToggle && !isEditing ? "pointer" : "default",
              userSelect: "none",
            }}
            onClick={() => {
              if (!isEditing && canToggle) toggleNode(node.id);
            }}
            title={canToggle ? "Click to expand/collapse" : ""}
          >
            {isEditing ? (
              <input
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(59,130,246,0.5)",
                  background: "rgba(255,255,255,0.1)",
                  outline: "none",
                  fontSize: 14,
                  fontWeight: 500,
                }}
                value={nodeDraft[node.id] ?? ""}
                onChange={(e) =>
                  setNodeDraft((prev) => ({ ...prev, [node.id]: e.target.value }))
                }
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEditNode(node.id);
                  if (e.key === "Escape") cancelEditNode();
                }}
              />
            ) : (
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {node.name || "—"}
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => saveEditNode(node.id)}
                  title="Save"
                  style={{ background: "#10b981", color: "white" }}
                >
                  <Save size={16} />
                </button>
                <button
                  type="button"
                  className="icon-btn ghost"
                  onClick={cancelEditNode}
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="icon-btn ghost"
                  onClick={() => startEditNode(node)}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="icon-btn danger"
                  onClick={() => deleteNode(node.id)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>

                {node.node_type !== "part" && (
                  <button
                    type="button"
                    className="add-button"
                    style={{
                      padding: "6px 10px",
                      fontSize: 12,
                      background: "rgba(59,130,246,0.1)",
                      border: "1px solid rgba(59,130,246,0.3)",
                      color: "#3b82f6",
                      boxShadow: "none",
                    }}
                    onClick={() =>
                      openAdd({
                        assetId,
                        parentId: node.id,
                        nodeType: "section",
                      })
                    }
                    title="Add Section"
                  >
                    <Plus size={14} /> Section
                  </button>
                )}

                <button
                  type="button"
                  className="add-button"
                  style={{
                    padding: "6px 10px",
                    fontSize: 12,
                    background: "rgba(139,92,246,0.1)",
                    border: "1px solid rgba(139,92,246,0.3)",
                    color: "#8b5cf6",
                    boxShadow: "none",
                  }}
                  onClick={() =>
                    openAdd({ assetId, parentId: node.id, nodeType: "part" })
                  }
                  title="Add Part"
                >
                  <Plus size={14} /> Part
                </button>
              </>
            )}
          </div>
        </div>

        {adding &&
          adding.assetId === assetId &&
          (adding.parentId ?? null) === node.id && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                marginBottom: 8,
                marginLeft: 24,
                borderRadius: 10,
                border: "2px dashed rgba(34,197,94,0.4)",
                background: "rgba(34,197,94,0.05)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: "rgba(34,197,94,0.15)",
                  color: "#10b981",
                  border: "1px solid rgba(34,197,94,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <Plus size={14} /> New {adding.nodeType}
              </div>

              <input
                style={{
                  flex: 1,
                  minWidth: 300,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(34,197,94,0.4)",
                  background: "rgba(255,255,255,0.08)",
                  outline: "none",
                  fontSize: 14,
                }}
                value={adding.name}
                onChange={(e) =>
                  setAdding((p) => ({ ...p, name: e.target.value }))
                }
                placeholder={`Enter ${adding.nodeType} name...`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") createNode();
                  if (e.key === "Escape") cancelAdd();
                }}
              />

              <button
                type="button"
                className="icon-btn"
                onClick={createNode}
                title="Save"
                style={{ background: "#10b981", color: "white" }}
              >
                <Save size={16} />
              </button>
              <button
                type="button"
                className="icon-btn ghost"
                onClick={cancelAdd}
                title="Cancel"
              >
                <X size={16} />
              </button>
            </div>
          )}

        {hasKids && isOpen && (
          <div style={{ position: "relative" }}>
            {kids.map((k) => (
              <NodeRow key={k.id} assetId={assetId} node={k} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="main-content">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>
            🏭 Asset Tree
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            Manage your assets with hierarchical sections and parts
          </p>
        </div>
        <Link
          to="/add-asset"
          className="add-button"
          style={{ padding: "10px 18px", fontSize: 14 }}
        >
          <Plus size={16} /> Add Asset
        </Link>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: "center", opacity: 0.6 }}>
          Loading assets...
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {assets.map((a) => {
          const isOpen = openAssets.has(a.id);
          const map = childrenMapByAsset.get(a.id);
          const roots = map?.get(null) || [];
          const isEditingAsset = editingAssetId === a.id;

          return (
            <div
              key={a.id}
              style={{
                borderRadius: 16,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "rgba(255,255,255,0.04)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "16px 18px",
                  background:
                    "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.08) 100%)",
                  borderBottom: isOpen
                    ? "1px solid rgba(148,163,184,0.2)"
                    : "none",
                }}
              >
                <button
                  type="button"
                  className="icon-btn ghost"
                  onClick={() => toggleAsset(a.id)}
                  title={isOpen ? "Collapse" : "Expand"}
                  style={{ width: 32, height: 32 }}
                >
                  {isOpen ? (
                    <ChevronDown size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </button>

                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <Building2 size={22} />
                </div>

                <div style={{ flex: 1 }}>
                  {isEditingAsset ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <input
                        value={assetDraft.asset_name}
                        onChange={(e) =>
                          setAssetDraft((p) => ({
                            ...p,
                            asset_name: e.target.value,
                          }))
                        }
                        placeholder="Asset name"
                        style={{ padding: "8px 10px", borderRadius: 10 }}
                      />

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        <input
                          value={assetDraft.location}
                          onChange={(e) =>
                            setAssetDraft((p) => ({
                              ...p,
                              location: e.target.value,
                            }))
                          }
                          placeholder="Location"
                          style={{ padding: "8px 10px", borderRadius: 10 }}
                        />

                        <input
                          value={assetDraft.machine_type}
                          onChange={(e) =>
                            setAssetDraft((p) => ({
                              ...p,
                              machine_type: e.target.value,
                            }))
                          }
                          placeholder="Machine type"
                          style={{ padding: "8px 10px", borderRadius: 10 }}
                        />

                        <input
                          value={assetDraft.line}
                          onChange={(e) =>
                            setAssetDraft((p) => ({
                              ...p,
                              line: e.target.value,
                            }))
                          }
                          placeholder="Line"
                          style={{ padding: "8px 10px", borderRadius: 10 }}
                        />
                      </div>

                      <input
                        value={assetDraft.description}
                        onChange={(e) =>
                          setAssetDraft((p) => ({
                            ...p,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Description (optional)"
                        style={{ padding: "8px 10px", borderRadius: 10 }}
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          marginBottom: 4,
                        }}
                      >
                        {a.asset_name || "Unnamed Asset"}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.65 }}>
                        ID: {a.id} • Location: {a.location || "—"} • Machine:{" "}
                        {a.machine_type || "—"} • Line: {a.line || "—"}
                      </div>
                    </>
                  )}
                </div>

                {/* ✅ Asset actions */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {isEditingAsset ? (
                    <>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => saveEditAsset(a.id)}
                        title="Save Asset"
                        style={{ background: "#10b981", color: "white" }}
                      >
                        <Save size={18} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn ghost"
                        onClick={cancelEditAsset}
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="icon-btn ghost"
                        onClick={() => startEditAsset(a)}
                        title="Edit Asset"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => deleteAsset(a.id)}
                        title="Delete Asset"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>

                {/* tree add buttons only when open and NOT editing asset */}
                {isOpen && !isEditingAsset && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="add-button"
                      onClick={() =>
                        openAdd({ assetId: a.id, parentId: null, nodeType: "section" })
                      }
                      style={{
                        padding: "8px 14px",
                        background: "rgba(59,130,246,0.15)",
                        border: "1px solid rgba(59,130,246,0.3)",
                        color: "#3b82f6",
                        boxShadow: "none",
                      }}
                    >
                      <Plus size={16} /> Section
                    </button>
                    <button
                      type="button"
                      className="add-button"
                      onClick={() =>
                        openAdd({ assetId: a.id, parentId: null, nodeType: "part" })
                      }
                      style={{
                        padding: "8px 14px",
                        background: "rgba(139,92,246,0.15)",
                        border: "1px solid rgba(139,92,246,0.3)",
                        color: "#8b5cf6",
                        boxShadow: "none",
                      }}
                    >
                      <Plus size={16} /> Part
                    </button>
                  </div>
                )}
              </div>

              {/* Tree area */}
              {isOpen && (
                <div style={{ padding: 18 }}>
                  {/* Root add form */}
                  {adding &&
                    adding.assetId === a.id &&
                    (adding.parentId ?? null) === null && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "12px 14px",
                          marginBottom: 12,
                          borderRadius: 10,
                          border: "2px dashed rgba(34,197,94,0.4)",
                          background: "rgba(34,197,94,0.05)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 6,
                            background: "rgba(34,197,94,0.15)",
                            color: "#10b981",
                            border: "1px solid rgba(34,197,94,0.3)",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Plus size={14} /> New {adding.nodeType}
                        </div>

                        <input
                          style={{
                            flex: 1,
                            minWidth: 300,
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px solid rgba(34,197,94,0.4)",
                            background: "rgba(255,255,255,0.08)",
                            outline: "none",
                            fontSize: 14,
                          }}
                          value={adding.name}
                          onChange={(e) =>
                            setAdding((p) => ({ ...p, name: e.target.value }))
                          }
                          placeholder={`Enter ${adding.nodeType} name...`}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") createNode();
                            if (e.key === "Escape") cancelAdd();
                          }}
                        />

                        <button
                          type="button"
                          className="icon-btn"
                          onClick={createNode}
                          title="Save"
                          style={{ background: "#10b981", color: "white" }}
                        >
                          <Save size={16} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn ghost"
                          onClick={cancelAdd}
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}

                  {roots.length > 0 ? (
                    <div style={{ position: "relative" }}>
                      {roots.map((n) => (
                        <NodeRow key={n.id} assetId={a.id} node={n} level={0} />
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: "32px 20px", textAlign: "center", opacity: 0.6 }}>
                      No structure yet. Click <strong>+ Section</strong> or{" "}
                      <strong>+ Part</strong> to begin building your asset tree.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {!loading && assets.length === 0 && (
          <div style={{ padding: 60, textAlign: "center", opacity: 0.6 }}>
            No assets found. Create your first asset to get started.
          </div>
        )}
      </div>
    </div>
  );
}

export default AllAssets;
